// Shared, stateless voice/record-card primitives for the Grok copilot.
//
// This is a LEAF module: it holds the pure types + helpers that BOTH the
// copilot component (`DashboardAIAssistant.tsx`) and the shared voice provider
// (`src/contexts/CopilotVoiceContext.tsx`) depend on. It exists to break what
// would otherwise be a circular import (component ⇄ context) AND to keep those
// helpers out of the lazy-loaded component chunk (the provider is eager). Keep
// it dependency-light — no React state, no DOM beyond WebAudio, no imports from
// the component or the context.

import { supabase } from "@/integrations/supabase/client";
import type { PlanStep, PlanStepResult } from "./PlanCard";
import type { CampaignDraft } from "./CampaignDraftCard";
// John-authored voice cues now live in the dependency-light leaf
// `src/lib/voiceCues.ts` (so the public site chat can reuse them without
// pulling in any CRM code). Re-exported below so existing CRM importers of
// these names from voiceShared keep working unchanged.
export {
  setVoiceCueVolume,
  isVoiceCueActive,
  preloadVoiceCues,
  playVoiceCue,
  startThinkingChime,
  stopThinkingChime,
} from "@/lib/voiceCues";

// Record types the assistant can link to via noknok://<type>/<uuid>
// markdown URLs. Kept narrow — only the CRM objects the detail drawer
// knows how to render.
export type RecordType = "lead" | "client" | "project" | "vendor" | "task" | "appointment" | "call" | "note";
// The entities the copilot can be SCOPED to (the Grok tab). "contact" is a
// related contact (lead_contacts) — a valid record-focus target on the backend,
// but not a card/record-drawer type, so it lives in its own union.
export type RecordScopeType = "lead" | "client" | "project" | "vendor" | "contact";

export const RECORD_LABEL: Record<RecordType, string> = {
  lead: "Lead",
  client: "Client",
  project: "Project",
  vendor: "Vendor",
  task: "Task",
  appointment: "Appointment",
  call: "Call",
  note: "Note",
};

// ── Card types ──────────────────────────────────────────────────────
// A small status/stage pill shown on a card. tone drives the color.
export type BadgeTone = "done" | "open" | "canceled" | "stage" | "neutral";
export interface CardBadge { text: string; tone: BadgeTone }
// A related record shown as its own row inside a record card.
export interface VoiceCardRelation { type: RecordType; id: string; label: string; badge?: CardBadge }
// A record surfaced by a tool call — rendered as a full-width card with a
// module header, the record link, and its relations stacked underneath.
export interface VoiceCard { type: RecordType; id: string; label: string; sub?: string; badge?: CardBadge; relations?: VoiceCardRelation[] }
// A line in the live voice transcript: spoken text, or a stack of record cards.
export interface VoiceTurn { role: "user" | "assistant" | "records"; text?: string; cards?: VoiceCard[] }

export interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  timestamp?: string;
  /** Record cards captured during a (voice) session — persisted with the
   *  thread so reopening a historical chat re-renders the same tappable
   *  records. A cards message has empty content. */
  cards?: VoiceCard[];
  /** Daily-briefing trigger turn — rendered as a subtle divider, not a
   *  user bubble (persisted by business-chat so history re-renders it too). */
  briefing?: boolean;
  /** Hidden staged-write marker persisted by business-chat (empty content).
   *  Surfaced compactly to the model on resume so it remembers what it
   *  previewed (plan details etc.) instead of re-asking the user. */
  preview?: { name: string; args?: Record<string, unknown> };
}

export interface ThreadSummary {
  id: string;
  title: string | null;
  updated_at: string;
  message_count: number;
  channel?: string;
  /** Record scope — set on threads started from a record's Grok tab so the
   *  GLOBAL history drawer can tag them ("· lead"). Null on global threads. */
  record_type?: RecordScopeType | null;
  record_id?: string | null;
}

// One write staged awaiting confirmation. The queue can hold several at once
// (e.g. "mark both tasks done" stages two), each rendered as its own card.
export type PendingPreview = {
  /** Unique per-card identity (render key + removal). NEVER share across cards —
   *  `key` (content hash) is only for de-dup/verbal-confirm matching; using it as
   *  the render/removal id made two same-shape cards collapse (confirm one → both
   *  closed). */
  id: string;
  key: string;
  name: string;
  action: string;
  title: string;
  fields: Record<string, unknown>;
  args: Record<string, unknown>;
  /** Multi-step plan preview (propose_plan) — renders as a PlanCard. */
  plan?: PlanStep[];
  /** Plan run state driven from OUTSIDE the card (tap AND verbal confirm both
   *  route through the context's executePlan, so the card animates the same
   *  either way). Absent → the card manages its own state (text mode). */
  planRun?: { running: boolean; results: PlanStepResult[] | null };
  /** SMS-campaign draft batch (draft_sms_campaign) — renders as a CampaignDraftCard. */
  drafts?: CampaignDraft[];
};

// Client-action hand-offs (send_sms / initiate_call render a small action
// card; attach_files opens the upload dialog directly).
export type SendFromOption = {
  provider: "dialpad" | "twilio";
  from_number_id: string; // "dialpad" for the user's Dialpad line, else twilio_numbers.id
  number?: string;
  label: string;
};
export type PendingActionState = {
  client_action: string;
  lead_id?: string;
  phone?: string;
  name?: string;
  message?: string;
  // The acting user's sending lines (server-resolved). The draft card's quick
  // "send via CRM" button only renders when there's EXACTLY one — with several,
  // the user picks a specific line in the options menu instead. No guessing.
  send_from_options?: SendFromOption[];
};

function chipName(r: Record<string, unknown>): string {
  const parts = [r.first_name, r.last_name].filter(Boolean) as string[];
  return parts.join(" ") || (r.business_name as string) || (r.name as string) || (r.phone as string) || (r.email as string) || "Record";
}

// "2026-07-02" + "09:00:00" → "Jul 2 · 9:00 AM" (compact card subtitle).
function cardWhen(date?: string | null, time?: string | null): string {
  if (!date) return "";
  const d = new Date(`${date}T${(time || "12:00").slice(0, 5)}:00`);
  if (Number.isNaN(d.getTime())) return [date, time?.slice(0, 5)].filter(Boolean).join(" · ");
  const day = d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  return time ? `${day} · ${d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}` : day;
}

// A project reference must ALWAYS carry its client name (John's hard rule),
// name FIRST: "Michelle Parr — PID: WH236".
function projectLabel(pid?: string | null, projectName?: string | null, clientName?: string | null): string {
  const idPart = pid ? `PID: ${pid}` : projectName || "Project";
  return clientName ? `${clientName} — ${idPart}` : String(idPart);
}

// ── Status / stage badges ──────────────────────────────────────────
// Tasks and appointments carry a completion status; projects carry a
// pipeline stage. Surface them as little pills on the card so the user
// sees state at a glance instead of the model reading it aloud.
function taskBadge(r: Record<string, unknown>): CardBadge {
  return r.is_completed ? { text: "Done", tone: "done" } : { text: "Open", tone: "open" };
}
const APPT_BADGE: Record<string, CardBadge> = {
  completed: { text: "Completed", tone: "done" },
  canceled: { text: "Canceled", tone: "canceled" },
  cancelled: { text: "Canceled", tone: "canceled" },
  rescheduled: { text: "Rescheduled", tone: "stage" },
  upcoming: { text: "Upcoming", tone: "open" },
};
function apptBadge(r: Record<string, unknown>): CardBadge {
  const s = String(r.status || "").toLowerCase();
  if (APPT_BADGE[s]) return APPT_BADGE[s];
  return r.is_completed ? { text: "Completed", tone: "done" } : { text: "Upcoming", tone: "open" };
}
// A project's pipeline stage. "Cancelled"/"lost"/"dead" stages read red.
function stageBadge(stage?: string | null): CardBadge | undefined {
  if (!stage) return undefined;
  const lost = /cancel|lost|dead|declined/i.test(stage);
  return { text: stage, tone: lost ? "canceled" : "stage" };
}

// Pull record CARDS out of a tool result so the transcript shows structured,
// tappable records (module header + record + relations) instead of the model
// reading raw UUIDs aloud. Covers list reads and single-record writes.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function extractRecordCards(name: string, result: any): VoiceCard[] {
  if (!result || result.error || result.requires_confirmation) return [];
  const out: VoiceCard[] = [];
  const seen = new Set<string>();
  const add = (card: VoiceCard) => {
    if (typeof card.id !== "string" || !card.id) return;
    const k = `${card.type}:${card.id}`;
    if (seen.has(k)) return;
    seen.add(k);
    out.push({ ...card, label: card.label || RECORD_LABEL[card.type] });
  };
  const contactRel = (r: Record<string, unknown>): VoiceCardRelation | null =>
    r.lead_id && r.lead_name
      ? { type: r.lead_is_client ? "client" : "lead", id: r.lead_id as string, label: r.lead_name as string }
      : null;

  // A project relation row, always naming the customer + carrying its stage.
  const projectRel = (r: Record<string, unknown>): VoiceCardRelation => ({
    type: "project",
    id: r.project_id as string,
    label: projectLabel(r.project_pid as string, r.project_name as string, (r.project_client_name || r.lead_name) as string),
    badge: stageBadge(r.project_stage as string),
  });

  if (name === "get_tasks" && Array.isArray(result.tasks)) {
    for (const r of result.tasks.slice(0, 6)) {
      const relations: VoiceCardRelation[] = [];
      // A project row ALWAYS names the customer — the project's own client
      // first, falling back to the task's direct contact — plus its stage.
      if (r.project_id) relations.push(projectRel(r));
      const c = contactRel(r);
      if (c) relations.push(c);
      add({ type: "task", id: r.id, label: (r.name as string) || "Task", sub: r.due_date ? `Due ${cardWhen(r.due_date, r.due_time)}` : undefined, badge: taskBadge(r), relations });
    }
    return out;
  }
  if (name === "get_appointments" && Array.isArray(result.appointments)) {
    // Appointments are `meetings` rows: name + start_date/start_time, related
    // to a lead/client or a project (whose client is flattened as lead_name).
    for (const r of result.appointments.slice(0, 6)) {
      const relations: VoiceCardRelation[] = [];
      if (r.project_id) relations.push(projectRel(r));
      const c = contactRel(r);
      if (c) relations.push(c);
      add({ type: "appointment", id: r.id, label: (r.name as string) || (r.lead_name ? `Meeting — ${r.lead_name}` : "Meeting"), sub: cardWhen(r.start_date, r.start_time) || undefined, badge: apptBadge(r), relations });
    }
    return out;
  }
  if (name === "get_projects" && Array.isArray(result.projects)) {
    for (const r of result.projects.slice(0, 6)) {
      const relations: VoiceCardRelation[] = [];
      if (r.client_lead_id && r.client_name) relations.push({ type: "client", id: r.client_lead_id as string, label: r.client_name as string });
      // Job-site address rides under the name (John's rule); the stage is now
      // a badge on the card instead of a text suffix.
      const sub = (r.address as string) || undefined;
      add({ type: "project", id: r.id, label: projectLabel(r.pid, r.name, r.client_name), sub, badge: stageBadge(r.stage as string), relations });
    }
    return out;
  }

  // Simple list reads → one card per record.
  const LISTS: Record<string, { key: string; type: RecordType; label: (r: Record<string, unknown>) => string; sub?: (r: Record<string, unknown>) => string | undefined }> = {
    get_clients: { key: "clients", type: "client", label: chipName, sub: (r) => (r.phone as string) || undefined },
    get_crm_leads: { key: "leads", type: "lead", label: chipName, sub: (r) => (r.phone as string) || undefined },
    get_vendors: { key: "vendors", type: "vendor", label: (r) => (r.company_name as string) || (r.vendor_name as string) || "Vendor" },
    get_lead_notes: { key: "notes", type: "note", label: (r) => ((r.content as string) || "Note").slice(0, 60) },
    get_lead_calls: { key: "calls", type: "call", label: (r) => (r.started_at ? new Date(r.started_at as string).toLocaleDateString() : "Call") },
    get_recent_calls: { key: "calls", type: "call", label: (r) => `${(r.lead_name as string) || "Call"}` },
  };
  const listCfg = LISTS[name];
  if (listCfg && Array.isArray(result[listCfg.key])) {
    for (const r of result[listCfg.key].slice(0, 8)) add({ type: listCfg.type, id: r.id, label: listCfg.label(r), sub: listCfg.sub?.(r) });
    return out;
  }

  // Single-record writes → { <key>: row } or ids.
  if (result.lead) add({ type: result.lead.is_client ? "client" : "lead", id: result.lead.id, label: chipName(result.lead) });
  if (result.client) add({ type: "client", id: result.client.id, label: chipName(result.client) });
  if (result.client_id) add({ type: "client", id: result.client_id, label: "New client" });
  if (result.task) {
    const t = result.task;
    // Attach the customer/project relations (same as get_tasks) so an UPDATED
    // task re-rendered as a card still names who it's for — not just on lists.
    const relations: VoiceCardRelation[] = [];
    if (t.project_id) relations.push(projectRel(t));
    const c = contactRel(t);
    if (c) relations.push(c);
    add({ type: "task", id: t.id, label: (t.name as string) || "Task", sub: t.due_date ? `Due ${cardWhen(t.due_date, t.due_time)}` : undefined, badge: taskBadge(t), relations });
  }
  if (result.appointment) {
    const a = result.appointment;
    const relations: VoiceCardRelation[] = [];
    if (a.project_id) relations.push(projectRel(a));
    const c = contactRel(a);
    if (c) relations.push(c);
    add({ type: "appointment", id: a.id, label: (a.name as string) || (a.lead_name ? `Meeting — ${a.lead_name}` : "Meeting"), sub: cardWhen(a.start_date, a.start_time) || undefined, badge: apptBadge(a), relations });
  }
  if (result.vendor) add({ type: "vendor", id: result.vendor.id, label: (result.vendor.company_name as string) || "Vendor" });
  if (result.project_id) add({ type: "project", id: result.project_id, label: "Project" });
  if (result.note) {
    // Show the note text (not the generic "Note") + WHO/WHAT it's for — the
    // server flattens lead_id/lead_name (and project_id/project_pid for a
    // PROJECT note) onto the created row so the card names the client or job.
    const n = result.note;
    const relations: VoiceCardRelation[] = [];
    if (n.project_id) relations.push(projectRel(n));
    const c = contactRel(n);
    if (c) relations.push(c);
    add({ type: "note", id: n.id, label: ((n.content as string) || "Note").slice(0, 60), relations });
  }
  return out;
}

// Plain-text form of a record card (for copy buttons / Copy Session).
export function cardToText(c: VoiceCard): string {
  const head = `[${RECORD_LABEL[c.type]}] ${c.label}${c.badge ? ` (${c.badge.text})` : ""}${c.sub ? ` — ${c.sub}` : ""}`;
  const rels = (c.relations ?? []).map((r) => `\n  ↳ ${RECORD_LABEL[r.type]}: ${r.label}${r.badge ? ` (${r.badge.text})` : ""}`).join("");
  return head + rels;
}

// Turn a live voice transcript into persistable chat messages (record turns
// become a cards message). Pure — shared by the provider's persistence + stop.
export function voiceTurnsToMessages(turns: VoiceTurn[]): ChatMessage[] {
  const now = new Date().toISOString();
  return turns
    .map((t): ChatMessage | null => {
      if (t.role === "records") {
        return t.cards?.length ? { role: "assistant", content: "", cards: t.cards, timestamp: now } : null;
      }
      return t.text ? { role: t.role, content: t.text, timestamp: now } : null;
    })
    .filter((m): m is ChatMessage => m !== null);
}

// Stable identity for a staged write, ignoring the `confirmed` flag — lets a
// later commit (same tool + args + confirmed:true) find and dismiss its card,
// and de-dupes if the same preview is emitted twice.
export function previewKey(name: string, args: Record<string, unknown>): string {
  const src = args && typeof args === "object" ? args : {};
  const entries = Object.keys(src)
    .filter((k) => k !== "confirmed")
    .sort()
    .map((k) => [k, (src as Record<string, unknown>)[k]]);
  return `${name}::${JSON.stringify(entries)}`;
}

// ── "Working on it" indicator label ─────────────────────────────────
// While a tool runs, the assistant says WHAT it's doing ("Checking your
// tasks…") — mapped from the tool name, with verb-prefix fallbacks.
const TOOL_ACTIVITY: Record<string, string> = {
  get_tasks: "Checking your tasks",
  get_appointments: "Checking the calendar",
  get_crm_leads: "Searching leads",
  get_clients: "Searching clients",
  get_projects: "Looking through projects",
  get_vendors: "Searching vendors",
  get_lead_notes: "Reading the notes",
  get_lead_activity: "Reviewing recent activity",
  get_lead_sms: "Reading the texts",
  get_recent_sms: "Reading recent texts",
  get_lead_calls: "Reviewing call history",
  get_recent_calls: "Reviewing recent calls",
  get_related_contacts: "Finding related contacts",
  get_lead_files: "Looking through files",
  list_team_members: "Checking the team",
  create_task: "Creating the task",
  update_task: "Updating the task",
  create_appointment: "Booking the appointment",
  update_appointment: "Updating the appointment",
  create_lead: "Creating the lead",
  create_lead_note: "Saving the note",
  create_related_contact: "Adding the contact",
  convert_lead_to_client: "Converting to client",
  send_sms: "Preparing the text",
  initiate_call: "Setting up the call",
};
export function toolActivityLabel(name?: string | null): string {
  if (name && TOOL_ACTIVITY[name]) return TOOL_ACTIVITY[name];
  if (!name) return "Working on it";
  if (/^(get|list|search|find)_/.test(name)) return "Looking that up";
  if (/^create_/.test(name)) return "Setting that up";
  if (/^(update|set|archive|add)_/.test(name)) return "Making the change";
  return "Working on it";
}

// Auth headers for the /api/ai/* Pages Functions — Bearer the current Supabase
// session. Stateless; shared by the copilot component (text) + the voice
// provider so there's ONE source of truth.
export async function getAuthHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) {
      headers["Authorization"] = `Bearer ${session.access_token}`;
    }
  } catch (e) {
    console.error("Failed to get auth session:", e);
  }
  return headers;
}

// Do two record scopes point at the same record? (business-agnostic — scope
// ids are globally unique). Used to decide "same call" vs "different record".
export function sameRecordScope(
  a: { type: RecordScopeType; id: string } | null | undefined,
  b: { type: RecordScopeType; id: string } | null | undefined,
): boolean {
  if (!a || !b) return a == null && b == null; // both global (null) counts as same
  return a.type === b.type && a.id === b.id;
}
